import {
  Box,
  Collapse,
  Divider,
  Flex,
  FormLabel,
  Image,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { Formik, Form } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import FallbackAvatar from 'components/FallbackAvatar';
import { FormikTextArea, FormikDatepicker, FormikSelectAsyncPaginate, FormikInput } from 'components/Formik';
import RadioInput from 'components/RadioCard';
import { timeOffDurationOptions } from 'containers/TimeOff/shared/options';
import { MONTH_DAY_YEAR_DASH_FORMAT } from 'global/appConstants';
import { fetchPaginatedCategory } from 'hooks/timeOff/useFetchPaginatedCategories';
import { IRecordTimeOffFormData, useRecordTimeOff } from 'hooks/timeOff/useRecordTimeOff';
import { useCompanyInfo } from 'store/useUserStore';
import { getAsyncSelectLoadOptions } from 'utils/helpers';

import { initialRecordTimeOffFormData, recordTimeOffFormValidationSchema } from './schema';

type TRecordTimeOffModalProps = {
  isLoading?: boolean;
  onClose: () => void;
  data: any;
};

const RecordTimeOffModal: React.FC<TRecordTimeOffModalProps> = ({ onClose, data }) => {
  const { t } = useTranslation(['common', 'timeOff']);

  const companyInfo = useCompanyInfo();

  const { isLoading, mutateAsync } = useRecordTimeOff();

  const loadCategoryOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedCategory, {
      page,
      search,
      active: '1',
    });
  };

  const handleCategoryChange = (
    selectedOption: any,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    if (selectedOption.in_hours) {
      setFieldValue('time_off_duration_options', timeOffDurationOptions);
    } else {
      setFieldValue(
        'time_off_duration_options',
        timeOffDurationOptions.filter(option => option.value === 'Full Day')
      );
      setFieldValue('requested_hours_type', 'Full Day');
    }
  };

  const handleFormSubmit = (values: typeof initialRecordTimeOffFormData) => {
    const postData: IRecordTimeOffFormData = {
      user_id: data?.user?.userId,
      category_id: values.category?.id,
      start: values.start_date ? format(values.start_date, MONTH_DAY_YEAR_DASH_FORMAT) : undefined,
      end: values.end_date ? format(values.end_date, MONTH_DAY_YEAR_DASH_FORMAT) : undefined,
      requested_hours_type: values?.requested_hours_type || 'Full Day',
      description: values?.description,
      ...(values.requested_hours_type === 'Specific Hours' ? { requested_hours: values?.requested_hours } : {}),
    };

    mutateAsync(postData).then(() => {
      onClose();
    });
  };

  return (
    <>
      <ModalHeader pb="0">Record time off</ModalHeader>
      <ModalBody>
        <Text>
          Recording time off for a user reduces their balance and creates an approved request for the selected dates in
          the system.
        </Text>
        <Divider my="5" borderBottomWidth="2px" borderStyle="dashed" />
        <Formik
          initialValues={{
            ...initialRecordTimeOffFormData,
            category: data?.category || null,
            time_off_duration_options: data?.category?.inHours
              ? timeOffDurationOptions
              : timeOffDurationOptions.filter(option => option.value === 'Full Day'),
          }}
          validationSchema={recordTimeOffFormValidationSchema}
          enableReinitialize
          onSubmit={handleFormSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form noValidate autoComplete="off">
              <Flex alignItems="center" mb="5">
                <Image
                  src={data?.user?.employee_image}
                  fallback={<FallbackAvatar name={data?.user?.firstName + ' ' + data?.user?.lastName} />}
                  alt={data?.user?.firstName + ' ' + data?.user?.lastName}
                  boxSize="60px"
                  objectFit="cover"
                  borderRadius="lg"
                />
                <Box ml="3" fontSize="md">
                  <Text fontWeight={700} color="newGray.1000">
                    {data?.user?.firstName + ' ' + data?.user?.lastName}
                  </Text>
                  <Text color="gray.500" fontSize="sm">
                    {data?.user?.roleName}
                  </Text>
                </Box>
              </Flex>

              <FormikSelectAsyncPaginate
                name="category"
                label={t('timeOff:timeOffCategory')}
                loadOptions={loadCategoryOptions}
                formControlProps={{ isRequired: true }}
                onChangeCallback={selectedOption => handleCategoryChange(selectedOption, setFieldValue)}
                selectProps={{
                  placeholder: t('timeOff:selectCategory'),
                  isMulti: false,
                  getOptionLabel: (option: any) => option.name,
                  getOptionValue: (option: any) => option.id,
                }}
              />

              <Divider my="5" borderBottomWidth="2px" borderStyle="dashed" />

              <Flex justifyContent="space-between">
                <Box mr={3}>
                  <FormikDatepicker
                    label={t('timeOff:timeOffStart')}
                    name="start_date"
                    datepickerProps={{
                      dateFormat: companyInfo?.companyDateFormat,
                      startDate: (values.start_date as Date) || null,
                      endDate: (values.end_date as Date) || null,
                      isClearable: false,
                      popperPlacement: 'bottom-start',
                    }}
                    formControlProps={{
                      isRequired: true,
                    }}
                  />
                </Box>
                <Box ml={3}>
                  <FormikDatepicker
                    label={t('timeOff:timeOffEnd')}
                    name="end_date"
                    datepickerProps={{
                      startDate: (values.start_date as Date) || null,
                      endDate: (values.end_date as Date) || null,
                      minDate: (values.start_date as Date) || null,
                      dateFormat: companyInfo?.companyDateFormat,
                      isClearable: false,
                      popperPlacement: 'bottom-end',
                    }}
                    formControlProps={{
                      isRequired: true,
                    }}
                  />
                </Box>
              </Flex>
              <Box mb={6}>
                <FormLabel fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight={600}>
                  {t('timeOff:timeOffDuration')} (Per Day)
                </FormLabel>
                <RadioInput
                  variant="secondary"
                  options={values.time_off_duration_options}
                  name="requested_hours_type"
                  defaultValue={values.requested_hours_type}
                  handleChange={(e: any) => {
                    setFieldValue('requested_hours_type', e);
                  }}
                />
              </Box>

              <Collapse in={values.requested_hours_type === 'Specific Hours'} unmountOnExit={true} animateOpacity>
                <FormikInput
                  type="number"
                  name="requested_hours"
                  label={t('timeOff:timeoffDuration(InHrs)')}
                  formControlProps={{
                    isRequired: true,
                  }}
                />
              </Collapse>

              <FormikTextArea name="description" label="Comment" formControlProps={{ isRequired: true }} />
              <ModalFooter>
                <Flex justifyContent="flex-end">
                  <Button variant="ghost" onClick={onClose} size="lg" mr="4">
                    {t('common:cancel')}
                  </Button>
                  <Button
                    type="submit"
                    size="lg"
                    fontWeight="semibold"
                    isLoading={isLoading}
                    bg="rgba(72,82, 175, 0.1)"
                    color="primary.500"
                    _hover={{
                      bg: 'rgba(72,82, 175, 0.2)',
                    }}
                    _active={{
                      bg: 'rgba(72,82, 175, 0.2)',
                    }}
                  >
                    {t('common:save')}
                  </Button>
                </Flex>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </>
  );
};

export default RecordTimeOffModal;
